import { useEffect, useSyncExternalStore } from 'react'
import { useAuth } from '../contexts/AuthContext'
import type { RentalAccessStatus } from './rentalAccessStore'
import {
    getRentalTenantAccessSnapshot,
    loadRentalTenantAccess,
    subscribeRentalTenantAccess,
} from './rentalTenantAccessStore'

/**
 * Se o utilizador deve ver as páginas do inquilino em Arrendamento ("Meu arrendamento" e "Minha
 * conta"): tem um arrendamento como inquilino OU já o teve (contratos terminados). Quem nunca arrendou
 * a casa de ninguém não as vê — não há nada para consultar nem para pagar ali.
 *
 * A resposta é a do servidor (`GET /api/arrendamentos/inquilino/acesso`), partilhada por toda a app
 * (Sidebar, CondominiumLayout, índice do módulo) — um só pedido por utilizador + condomínio. Estados,
 * tal como em `useRentalOwnerAccess`:
 * - `loading`  → ainda sem resposta (o menu esconde-se, a rota espera).
 * - `visible` / `hidden` → resposta do servidor.
 * - `error`    → o pedido falhou: o menu esconde-se, mas a rota NÃO é bloqueada (o servidor continua a
 *                ser a autoridade — a quem não é inquilino só devolve listas vazias).
 */
export function useRentalTenantAccess(): { status: RentalAccessStatus; canSeeTenantPages: boolean } {
    const { user, hasPermission } = useAuth()
    const current = useSyncExternalStore(
        subscribeRentalTenantAccess, getRentalTenantAccessSnapshot, getRentalTenantAccessSnapshot)

    // Sem entidade no condomínio, ou sem nenhuma das permissões do módulo, não há nada a perguntar ao servidor.
    const applicable = !!user?.temEntidade && !!user.estateId
        && (hasPermission('ARRENDAMENTO_CONSULTAR_PROPRIO') || hasPermission('ARRENDAMENTO_GERIR_PROPRIO'))
    const key = applicable ? `${user.id}:${user.estateId}` : null

    useEffect(() => {
        if (key) loadRentalTenantAccess(key)
    }, [key])

    const status: RentalAccessStatus = !applicable ? 'hidden' : current.key === key ? current.status : 'loading'
    return { status, canSeeTenantPages: status === 'visible' }
}
